let dayjs = require('dayjs').default
if (!dayjs) dayjs = require('dayjs')
/**
 *  parseLatestEligibleDates will get the most recent effectiveDate from a patient
 *  array of eligibility or tc91 dates. Then if it finds duplicate effectiveDates
 *  the logic is to default to the nearest one to today's date.
 *
 *  @constructor
 *  @param {array} patientArrayOfDates The array of dates to be processed.
 *  @param {boolean} ignoreOldDates Whether to ignore old date formats (this is
 *                                  useful on tc91 parsing since we don't consider
 *                                  these old date formats relevant)
 *  @returns {object} A key pair of {effectiveDate, terminationDate}
 */
const parseLatestEligibleDates = (dates, ignoreOldDates) => {
  if (!Array.isArray(dates)) return null
  if (dates.length === 0) return null

  // find death codes
  const deathOption = dates.find(d => String(d.disenrollCode) === '1')
  if (deathOption) return deathOption

  var latest = dates
    .filter(function(item) {
      // handle weird old date formats that have non-updated data.
      if (ignoreOldDates && (item.effectiveDate.length !== 10 || item.terminationDate.length !== 10)) {
        return false
      }

      // weird thing where 2017-01 dates had this in the hicn field. send these
      // to the bottom.
      if (item.effectiveDate === item.terminationDate) {
        return false
      }

      // ignore future dates
      if (dayjs(item.effectiveDate).isAfter(dayjs(), 'day')) {
        return false
      }

      return true
    })
    .concat()
    .sort(function(first, second) {
      // sort in reverse order
      var firstEffective = dayjs(first.effectiveDate).format('YYYY-MM-DD')
      var secondEffective = dayjs(second.effectiveDate).format('YYYY-MM-DD')
      var firstTermination = dayjs(first.terminationDate).format('YYYY-MM-DD')
      var secondTermination = dayjs(second.terminationDate).format('YYYY-MM-DD')

      if (secondEffective === firstEffective) {
        // this could mean that a termination date was set,
        // and that this would take precedence
        if (dayjs(secondTermination).isBefore(firstTermination)) return 1
        if (dayjs(secondTermination).isAfter(firstTermination)) return -1
      }

      if (dayjs(secondEffective).isAfter(firstEffective)) return 1
      if (dayjs(secondEffective).isBefore(firstEffective)) return -1

      return 0
    })[0]

  return latest
}

/**
 *  Check if a patient is eligible.
 *  1. Get the patient's latesst eligible dates.
 *  2. if the term date > effective date:
 *    2.a. eligible if today is between effective and term dates.
 *
 *  @param {object} patient The patient model, should have eligibility
 *  @returns {boolean}
 */
const isPatientEligible = (patient, ignoreWorkflow) => {
  if (
    !patient ||
    !patient.eligibility ||
    !patient.currentWorkflowState ||
    !patient.currentWorkflowState.name ||
    (!ignoreWorkflow && patient.currentWorkflowState.name.includes('Disenroll'))
  ) {
    return false
  }

  let today = Date.parse(dayjs().format('YYYY-MM-DD'))

  const eligibility = parseLatestEligibleDates(patient.eligibility)

  if (!eligibility) return false

  // apply the rules here now that we have the latest eligibility dates
  // term should be after
  if (Date.parse(eligibility.terminationDate) > Date.parse(eligibility.effectiveDate)) {
    if (
      // dates should be within range of today
      // or they can be future dates (this is ok apparently)
      Date.parse(eligibility.effectiveDate) <= today &&
      Date.parse(eligibility.terminationDate) >= today
    ) {
      return true
    }
  }

  return false
}

/**
 *  This is a helper function for checking if patient is in EMTM. I made this
 *  separate from eligibility, because I feel like it will only be used sometimes
 *  when filtering out certain patients. It is also very specific criteria
 *  that could change in the future, and would not want it integrated everywhere.
 *
 *  @param {Object} patient The patient from map-patient
 *  @returns True or false
 */
const inNPAStandard = patient => {
  if (patient.metadata && patient.metadata.type3) {
    if (patient.metadata.type3.planId !== '001') return false
    if (patient.metadata.type3.group !== 'D251000') return false
    if (patient.metadata.type3.carrier !== '8633') return false
  }

  return true
}

/**
 * Check if a patient is in a hospice state
 * @param  {Object}  patient The patient in question. this should come from eligibility
 * @return {Boolean}         Returns `true` if not in hospice
 */
const isPatientNotHospice = patient => {
  var notHospice = true
  var today = dayjs().format('YYYY-MM-DD')

  if (patient.hospice) {
    patient.hospice.forEach(function(record) {
      if (record.fromDate <= today && record.throughDate >= today) {
        notHospice = false
      }
    })
  }

  return notHospice
}

module.exports = {
  isPatientEligible,
  parseLatestEligibleDates,
  inNPAStandard,
  isPatientNotHospice,
  fileDates: require('./fileDates'),
  type3: require('./type3'),
  type5: require('./type5')
}
